import { LocalParticipant, Participant } from 'livekit-client';
import { selectLocalParticipant, selectRoomMetadata } from 'state/selectors';
import { getParticipantUserIdFromMetadata } from 'useCase/participant/useParticipantMetadata';
import { isModeratorParticipant } from 'useCase/moderation/status/moderators';
import type { WebinarModeData, WebinarSpeaker } from 'api/types';

export const getWebinarMode = (): WebinarModeData | undefined => {
    return selectRoomMetadata()?.webinarMode;
};

export const isWebinarEnabled = (): boolean => {
    return Boolean(getWebinarMode()?.enabled);
};

export const isWebinarMicDisabled = (): boolean => {
    return Boolean(getWebinarMode()?.microDisabled);
};

export const isWebinarCameraDisabled = (): boolean => {
    return Boolean(getWebinarMode()?.cameraDisabled);
};

export const isWebinarScreenShareDisabled = (): boolean => {
    return Boolean(getWebinarMode()?.screenShareDisabled);
};

const getParticipantUserId = (participant: Participant): string | null =>
    getParticipantUserIdFromMetadata(participant.metadata);

export const isParticipantSpeaker = (participant: Participant): boolean => {
    const speakers = getWebinarMode()?.speakers;
    if (!Array.isArray(speakers) || speakers.length === 0) return false;

    const userId = getParticipantUserId(participant);
    const identity = participant.identity || null;

    return speakers.some((s) => matchesWebinarSpeaker(s, { userId, identity }));
};

export const isLocalUserSpeaker = (): boolean => {
    const local = selectLocalParticipant();
    if (!local) return false;
    return isParticipantSpeaker(local);
};

/** Слушатель вебинара: не модератор и не спикер. */
export const isWebinarListenerParticipant = (participant: Participant): boolean => {
    if (!isWebinarEnabled()) return false;
    return !isModeratorParticipant(participant) && !isParticipantSpeaker(participant);
};

export const isLocalWebinarListener = (): boolean => {
    const local = selectLocalParticipant();
    if (!local) return false;
    return isWebinarListenerParticipant(local);
};

const pickWebinarSpeakerFocusParticipant = (
    participants: Participant[],
    recentSpeakerId?: string | null,
): Participant | undefined => {
    const isEligibleFocus = (participant: Participant) =>
        !participant.isLocal || !isWebinarListenerParticipant(participant);

    if (recentSpeakerId) {
        const recent = participants.find(({ identity }) => identity === recentSpeakerId);
        if (recent && isEligibleFocus(recent)) return recent;
    }

    const screenSharer = participants.find(({ isScreenShareEnabled }) => isScreenShareEnabled);
    if (screenSharer) return screenSharer;

    const privilegedSpeaking = participants.find(
        (participant) =>
            isEligibleFocus(participant) &&
            participant.isSpeaking &&
            (isModeratorParticipant(participant) || isParticipantSpeaker(participant)),
    );
    if (privilegedSpeaking) return privilegedSpeaking;

    const privileged = participants.find(
        (participant) =>
            isEligibleFocus(participant) && (isModeratorParticipant(participant) || isParticipantSpeaker(participant)),
    );
    if (privileged) return privileged;

    const remoteSpeaking = participants.find(({ isLocal, isSpeaking }) => !isLocal && isSpeaking);
    if (remoteSpeaking) return remoteSpeaking;

    return participants.find(({ isLocal }) => !isLocal) ?? participants.find(isEligibleFocus);
};

/** Слушатель вебинара не должен быть в главном окне speaker view. */
export const reorderForWebinarSpeakerFocus = (
    participants: Participant[],
    recentSpeakerId?: string | null,
): Participant[] => {
    const local = participants.find(({ isLocal }) => isLocal);
    if (!local || !isWebinarListenerParticipant(local)) return participants;

    const focus = pickWebinarSpeakerFocusParticipant(participants, recentSpeakerId);
    if (!focus || focus.identity === participants[0]?.identity) return participants;

    return [focus, ...participants.filter(({ identity }) => identity !== focus.identity)];
};

/**
 * Жёсткая блокировка микрофона: вебинар включён, мик заблокирован, участник не модератор и не спикер.
 */
export const isMicHardLocked = (participant: Participant): boolean => {
    if (!isWebinarEnabled() || !isWebinarMicDisabled()) return false;
    return !isModeratorParticipant(participant) && !isParticipantSpeaker(participant);
};

/**
 * Жёсткая блокировка камеры.
 */
export const isCameraHardLocked = (participant: Participant): boolean => {
    if (!isWebinarEnabled() || !isWebinarCameraDisabled()) return false;
    return !isModeratorParticipant(participant) && !isParticipantSpeaker(participant);
};

/**
 * Жёсткая блокировка демонстрации экрана.
 */
export const isScreenShareHardLocked = (participant: Participant): boolean => {
    if (!isWebinarEnabled() || !isWebinarScreenShareDisabled()) return false;
    return !isModeratorParticipant(participant) && !isParticipantSpeaker(participant);
};

export const stopScreenShareIfHardLocked = (participant: LocalParticipant): void => {
    if (!isScreenShareHardLocked(participant) || !participant.isScreenShareEnabled) return;

    void participant.setScreenShareEnabled(false).catch((error) => {
        console.error('[Webinar] не удалось остановить демонстрацию экрана', error);
    });
};

export const stopCameraIfHardLocked = (
    participant: LocalParticipant,
    setVideoEnabled?: (enabled: boolean) => void,
): void => {
    if (!isCameraHardLocked(participant)) return;

    setVideoEnabled?.(false);

    if (!participant.isCameraEnabled) return;

    void participant.setCameraEnabled(false).catch((error) => {
        console.error('[Webinar] не удалось выключить камеру', error);
    });
};

/** Слушатель для FORCE_* broadcast: роль без isWebinarEnabled — metadata может ещё не лежать в сторе. */
const isForceLockTarget = (participant: Participant): boolean =>
    !isModeratorParticipant(participant) && !isParticipantSpeaker(participant);

/** Для broadcast-команды до синхронизации metadata (cameraDisabled ещё не в roomInfoStore). */
export const forceStopCameraForWebinarListener = (
    participant: LocalParticipant,
    setVideoEnabled?: (enabled: boolean) => void,
): void => {
    if (!isForceLockTarget(participant) || !participant.isCameraEnabled) return;

    void participant.setCameraEnabled(false).catch((error) => {
        console.error('[Webinar] не удалось выключить камеру', error);
    });
    setVideoEnabled?.(false);
};

export const stopMicIfHardLocked = (
    participant: LocalParticipant,
    setAudioEnabled?: (enabled: boolean) => void,
): void => {
    if (!isMicHardLocked(participant)) return;

    setAudioEnabled?.(false);

    if (!participant.isMicrophoneEnabled) return;

    void participant.setMicrophoneEnabled(false).catch((error) => {
        console.error('[Webinar] не удалось выключить микрофон', error);
    });
};

export const forceStopMicForWebinarListener = (
    participant: LocalParticipant,
    setAudioEnabled?: (enabled: boolean) => void,
): void => {
    if (!isForceLockTarget(participant) || !participant.isMicrophoneEnabled) return;

    void participant.setMicrophoneEnabled(false).catch((error) => {
        console.error('[Webinar] не удалось выключить микрофон', error);
    });
    setAudioEnabled?.(false);
};

export const getSpeakerIdentityForApi = (participant: Participant): WebinarSpeaker => {
    const userId = getParticipantUserId(participant);
    // USER: только userId (identity null) — как moderators в metadata, mobile матчит иконку/нотификацию.
    if (userId) {
        return { userId, identity: null };
    }
    return { identity: participant.identity || null };
};

export const matchesWebinarSpeaker = (speaker: WebinarSpeaker, candidate: WebinarSpeaker): boolean => {
    return (
        Boolean(candidate.userId && speaker.userId === candidate.userId) ||
        Boolean(candidate.identity && speaker.identity === candidate.identity)
    );
};
